import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { Combobox } from '@/components/common/Combobox'
import { RichTextEditor } from '@/components/common/RichTextEditor'
import { useAuthStore } from '@/features/auth/store'
import { useCreateTask } from '../hooks'
import type { AssigneeInput, Priority, TaskContext } from '../types'
import { ActionTypeSelect } from './ActionTypeSelect'
import { AssigneePicker } from './AssigneePicker'

type Props = {
  /** Gdy podany - task tworzony w kontekście (pole nieedytowalne, pokazany label). */
  context?: TaskContext
  onClose: () => void
}

export function CreateTaskDialog({ context, onClose }: Props) {
  const user = useAuthStore(s => s.user)
  const create = useCreateTask()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [priority, setPriority] = useState<Priority>('NORMAL')
  const [actionTypeCode, setActionTypeCode] = useState<string | null>(null)
  // Default: zalogowany user na siebie. Picker dopuszcza zmianę na inną osobę lub zespół.
  const [assignee, setAssignee] = useState<AssigneeInput | null>(
    user ? { type: 'USER', userId: user.id, userName: user.fullName } : null,
  )
  const [error, setError] = useState<string | null>(null)

  const submit = () => {
    setError(null)
    if (!title.trim()) {
      setError('Podaj tytuł zadania.')
      return
    }
    if (!assignee) {
      setError('Wybierz osobę lub zespół.')
      return
    }
    create.mutate(
      {
        title: title.trim(),
        description: description.trim() || undefined,
        dueDate: dueDate || undefined,
        priority,
        assignee,
        context,
        actionTypeCode: actionTypeCode ?? undefined,
      },
      { onSuccess: onClose, onError: () => setError('Nie udało się utworzyć zadania.') },
    )
  }

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[480px] shadow-xl">
          <Dialog.Title className="text-base font-semibold">Nowe zadanie</Dialog.Title>
          <div className="mt-4 flex flex-col gap-3 text-sm">
            {context && (
              <p className="text-xs text-slate-500">
                Kontekst: <span className="font-medium">{context.label}</span>
              </p>
            )}
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Tytuł</span>
              <input value={title} onChange={e => setTitle(e.target.value)}
                className="border rounded px-2 py-1" />
            </label>
            <div className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Opis</span>
              <RichTextEditor
                value={description}
                onChange={setDescription}
                placeholder="Opis zadania (możesz formatować: pogrubienie, listy, linki...)"
              />
            </div>
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Termin</span>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                className="border rounded px-2 py-1" />
            </label>
            <div className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Priorytet</span>
              <Combobox<Priority>
                value={priority}
                onChange={(v) => { if (v) setPriority(v) }}
                options={[
                  { value: 'LOW', label: 'Niski' },
                  { value: 'NORMAL', label: 'Normalny' },
                  { value: 'HIGH', label: 'Wysoki' },
                ]}
                placeholder="Wybierz priorytet..."
              />
            </div>
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Typ akcji</span>
              <ActionTypeSelect value={actionTypeCode} onChange={setActionTypeCode} />
            </label>
            <div className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Przypisany do</span>
              <AssigneePicker value={assignee} onChange={setAssignee} />
            </div>
            {error && <p className="text-xs text-red-600">{error}</p>}
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={onClose} className="px-3 py-1 text-sm border rounded">Anuluj</button>
              <button onClick={submit} disabled={!title.trim() || !assignee || create.isPending}
                className="px-3 py-1 text-sm bg-sky-600 text-white rounded disabled:opacity-50">
                Utwórz
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
